import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Accessibility as A11yIcon, Inbox, Package, Shirt, LayoutGrid, Image, Users, Settings, KeyRound, LogOut, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AdminLogin from "@/pages/admin/AdminLogin";
import OrdersTab from "@/pages/admin/OrdersTab";
import ProductsTab from "@/pages/admin/ProductsTab";
import RoomsTab from "@/pages/admin/RoomsTab";
import ImageryTab from "@/pages/admin/ImageryTab";
import TeamTab from "@/pages/admin/TeamTab";
import SettingsTab from "@/pages/admin/SettingsTab";
import AccountTab from "@/pages/admin/AccountTab";
import AccessibilityTab from "@/pages/admin/AccessibilityTab";
import InboxTab, { useInboxCount } from "@/pages/admin/InboxTab";

const TABS = [
  { to: "orders", label: "Orders", icon: Package, el: <OrdersTab /> },
  { to: "inbox", label: "Inbox", icon: Inbox, inbox: true },
  { to: "products", label: "Store items", icon: Shirt, el: <ProductsTab /> },
  { to: "rooms", label: "Rooms", icon: LayoutGrid, el: <RoomsTab /> },
  { to: "imagery", label: "Site imagery", icon: Image, el: <ImageryTab /> },
  { to: "team", label: "Team", icon: Users, el: <TeamTab />, owner: true },
  { to: "settings", label: "Shipping & connections", icon: Settings, el: <SettingsTab />, owner: true },
  { to: "accessibility", label: "Accessibility", icon: A11yIcon, el: <AccessibilityTab /> },
  { to: "account", label: "My account", icon: KeyRound, el: <AccountTab /> },
];

function Shell() {
  const { user, logout } = useAuth();
  const ready = Boolean(user) && !user.must_change_password;
  const [newCount, refreshCount] = useInboxCount(ready);
  const [a11yDue, setA11yDue] = useState(false);
  useEffect(() => { if (ready) api.get("/admin/a11y").then((r) => setA11yDue(r.data.due)).catch(() => {}); }, [ready]);
  if (user === null) return <div className="min-h-screen bg-[#EDECE9]" />;
  if (!user) return <Navigate to="/admin/login" replace />;
  const tabs = TABS.filter((t) => !t.owner || user.role === "owner");

  return (
    <div data-testid="admin-shell" className="flex min-h-screen flex-col bg-[#F2F1EE] md:flex-row">
      <aside className="border-b border-hairline bg-paper md:sticky md:top-0 md:h-screen md:w-64 md:shrink-0 md:border-b-0 md:border-r">
        <div className="px-6 py-6">
          <p className="overline text-clay">Studio admin</p>
          <p data-testid="admin-user-email" className="mt-2 truncate font-body text-xs text-muted">{user.email} · {user.role}</p>
        </div>
        <nav aria-label="Admin" className="flex gap-1 overflow-x-auto px-3 pb-3 md:flex-col md:overflow-visible">
          {!user.must_change_password && tabs.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={`/admin/${to}`} data-testid={`admin-tab-${to}`}
              className={({ isActive }) => `flex shrink-0 items-center gap-3 px-3 py-2.5 font-body text-sm transition-colors ${isActive ? "bg-ink text-paper" : "text-stone hover:bg-ink/5"}`}>
              <Icon className="h-4 w-4" strokeWidth={1.5} /> {label}
              {to === "accessibility" && a11yDue && (
                <span data-testid="admin-a11y-due" className="ml-auto rounded-full bg-clay px-2 py-0.5 font-mono text-[0.55rem] uppercase text-paper">Due</span>
              )}
              {to === "inbox" && newCount > 0 && (
                <span data-testid="admin-inbox-count" className="ml-auto rounded-full bg-clay px-2 py-0.5 font-mono text-[0.6rem] text-paper">{newCount}</span>
              )}
            </NavLink>
          ))}
          <Link to="/kingsley" target="_blank" data-testid="admin-view-store" className="flex shrink-0 items-center gap-3 px-3 py-2.5 font-body text-sm text-stone hover:bg-ink/5">
            <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} /> View store
          </Link>
          <button type="button" onClick={logout} data-testid="admin-logout" className="flex shrink-0 items-center gap-3 px-3 py-2.5 text-left font-body text-sm text-stone hover:bg-ink/5">
            <LogOut className="h-4 w-4" strokeWidth={1.5} /> Sign out
          </button>
        </nav>
      </aside>
      <main className="min-w-0 flex-1 px-5 py-8 md:px-10 md:py-10">
        {user.must_change_password ? (
          <AccountTab forced />
        ) : (
          <Routes>
            <Route index element={<Navigate to="/admin/orders" replace />} />
            {tabs.map((t) => <Route key={t.to} path={t.to} element={t.inbox ? <InboxTab onCountChange={refreshCount} /> : t.el} />)}
            <Route path="*" element={<Navigate to="/admin/orders" replace />} />
          </Routes>
        )}
      </main>
    </div>
  );
}

export default function AdminApp() {
  return (
    <Routes>
      <Route path="login" element={<AdminLogin />} />
      <Route path="*" element={<Shell />} />
    </Routes>
  );
}
